import type { ChordPayload, NotePayload } from '../../scheduler/actions';
import { MAX_MIDI_NOTE, MIN_MIDI_NOTE, type ChordQuality } from '../../format/types';
import type { OscillatorTypeLike } from './audioEnvironment';

/**
 * Preview-Klangzuordnung. Bewusst einfach (siehe LIMITATIONS.md):
 * - keine echte Skalen-Engine (die gehoert zu einem Instrument, nicht zur
 *   neutralen Sequenz),
 * - Unterscheidung von Rollen ueber Namenshinweise ("bass", "lead", ...) mit
 *   deterministischem Hash-Fallback - niemals ueber eine Instrument-ID.
 */

const CHORD_INTERVALS: Record<ChordQuality, readonly number[]> = {
  major: [0, 4, 7],
  minor: [0, 3, 7],
  diminished: [0, 3, 6],
  augmented: [0, 4, 8],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  major7: [0, 4, 7, 11],
  minor7: [0, 3, 7, 10],
  dominant7: [0, 4, 7, 10],
};

const MAJOR_SCALE_SEMITONES = [0, 2, 4, 5, 7, 9, 11];

const OSCILLATOR_ROTATION: readonly OscillatorTypeLike[] = ['sine', 'sawtooth', 'square', 'triangle'];

export function hashString(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash);
}

function degreeToMidi(degree: number, baseMidi: number): number {
  const zeroBased = degree - 1;
  const octaveShift = Math.floor(zeroBased / MAJOR_SCALE_SEMITONES.length);
  const index = ((zeroBased % MAJOR_SCALE_SEMITONES.length) + MAJOR_SCALE_SEMITONES.length) % MAJOR_SCALE_SEMITONES.length;
  const semitone = MAJOR_SCALE_SEMITONES[index] ?? 0;
  return baseMidi + octaveShift * 12 + semitone;
}

export function midiToFrequency(midi: number): number {
  const safeMidi = Number.isFinite(midi) ? Math.max(MIN_MIDI_NOTE, Math.min(MAX_MIDI_NOTE, midi)) : 69;
  return 440 * Math.pow(2, (safeMidi - 69) / 12);
}

export function notePayloadToMidi(payload: NotePayload): number {
  if (payload.note !== undefined) {
    return payload.note;
  }
  return degreeToMidi(payload.scaleDegree ?? 1, 60);
}

export function chordPayloadToMidiNotes(payload: ChordPayload): readonly number[] {
  const root = degreeToMidi(payload.degree, 48);
  const intervals = CHORD_INTERVALS[payload.quality];
  return intervals.map((interval) => root + interval);
}

export interface VoiceTimbre {
  readonly waveform: OscillatorTypeLike;
  readonly detuneCents: number;
}

/** Waehlt eine Klangfarbe anhand von Rollen-Namenshinweisen, sonst deterministisch per Hash der Track-ID. */
export function timbreForRole(role: string, trackId: string): VoiceTimbre {
  const lower = role.toLowerCase();
  if (lower.includes('bass')) return { waveform: 'sine', detuneCents: 0 };
  if (lower.includes('lead')) return { waveform: 'sawtooth', detuneCents: 0 };
  if (lower.includes('chord') || lower.includes('pad')) return { waveform: 'triangle', detuneCents: 0 };
  const hash = hashString(trackId);
  const waveform = OSCILLATOR_ROTATION[hash % OSCILLATOR_ROTATION.length] ?? 'sine';
  const detuneCents = (hash % 7) - 3;
  return { waveform, detuneCents };
}

export interface DrumVoiceSpec {
  readonly startFrequency: number;
  readonly endFrequency: number;
  readonly durationSeconds: number;
  readonly waveform: OscillatorTypeLike;
}

/** Kurzer, perkussiver Klick statt Sample-Wiedergabe (siehe LIMITATIONS.md). */
export function drumSpecForTrigger(triggerId: string): DrumVoiceSpec {
  const lower = triggerId.toLowerCase();
  if (lower.includes('kick') || lower.includes('bd')) {
    return { startFrequency: 150, endFrequency: 40, durationSeconds: 0.18, waveform: 'sine' };
  }
  if (lower.includes('snare') || lower.includes('sd')) {
    return { startFrequency: 400, endFrequency: 180, durationSeconds: 0.12, waveform: 'square' };
  }
  if (lower.includes('hat') || lower.includes('hh') || lower.includes('cym')) {
    return { startFrequency: 3200, endFrequency: 2400, durationSeconds: 0.05, waveform: 'square' };
  }
  const hash = hashString(triggerId);
  const startFrequency = 200 + (hash % 600);
  return { startFrequency, endFrequency: startFrequency * 0.4, durationSeconds: 0.1, waveform: 'triangle' };
}

export function velocityToGain(velocity: number): number {
  return Math.max(0, Math.min(1, velocity / 127));
}
